import React from 'react';
import { Link } from 'react-router-dom';
import { Gauge, ClipboardCheck, AlertTriangle, ShieldCheck, FileSpreadsheet, Cpu, Radio, MonitorSmartphone, BarChart3, ArrowRight } from 'lucide-react';
import { BRAND_GRADIENT } from '../components/BrandSplash';

const FEATURES = [
  [Gauge, 'Live OEE tracking', 'Real-time availability, performance and quality for every CNC / VMC machine, straight from the machine controller.'],
  [ClipboardCheck, 'Automatic job cards', 'Job cards fill themselves from machine cycles — parts, cycle time, shift and operator, with print-ready PDFs.'],
  [AlertTriangle, 'Loss management', 'Setup, tool, method, power and material losses recorded at the machine with alarm escalation.'],
  [ShieldCheck, 'Digital quality inspection', 'Setup approval by QA HOD, in-process checks every interval, tolerance alerts and quality hold.'],
  [FileSpreadsheet, 'Your own documents', 'Drawings and Excel inspection sheets stay in your company\u2019s own Google Drive. Reports keep your original format.'],
  [Cpu, 'Smart Controller', 'Plug-in edge device pairs to a machine by QR code and streams cycle and alarm signals securely.'],
];

const STEPS = [
  [Cpu, 'Fit the Smart Controller', 'A small controller is wired to the machine\u2019s cycle-complete (M30), cycle-start and alarm signals. Scan its QR code once to pair it with the machine.'],
  [Radio, 'Machine data flows in', 'Every cycle, stop and alarm is sent securely to Swami Software in real time. Nobody has to write anything down.'],
  [MonitorSmartphone, 'Operators tap, not write', 'At the machine, the operator starts the job card and taps the loss reason — Setup, Tool, Power, Material — when the machine stops.'],
  [ShieldCheck, 'QA approves the setup', 'Setup inspection opens automatically, QA HOD approves it digitally and in-process checks are reminded every two hours.'],
  [BarChart3, 'Live dashboard & reports', 'Managers see live OEE, colour-coded by target, and download Daily Production Reports and job cards in one click.'],
];

const NUMBERS = [
  ['15–25%', 'typical OEE improvement in the first months, once hidden losses become visible'],
  ['0', 'paper job cards to fill in by hand — counts come straight from the machine'],
  ['2 hrs', 'default in-process inspection interval, with automatic reminders'],
  ['1 click', 'to download the Daily Production Report and inspection reports in Excel'],
];
const BENEFITS = ['Know exactly why every machine stopped, by shift and operator', 'Cut setup time with measured setup losses and QA waiting time',
  'Stop bad parts early with tolerance alerts and quality hold', 'Replace manual registers and end-of-day data entry',
  'Plan better with accurate cycle times and parts per hour', 'Keep confidential drawings inside your own Google Drive'];

const H2 = ({ kicker, title }) => (
  <div className="mb-10">
    <p className="text-xs font-black uppercase tracking-[0.25em] text-blue-700 mb-3">{kicker}</p>
    <h2 className="text-3xl sm:text-4xl font-black max-w-3xl" style={{ fontFamily: 'Chivo' }}>{title}</h2>
  </div>
);

export default function Home() {
  return (
    <div className="min-h-screen bg-[#f7f7f4] text-slate-900" data-testid="public-home-page">
      <header className="sticky top-0 z-20 bg-[#f7f7f4]/85 backdrop-blur border-b border-slate-200/70 flex items-center justify-between px-6 md:px-12 py-3">
        <img src="/swami-logo.png" alt="Swami Software" className="h-11 w-auto" />
        <nav className="hidden md:flex gap-8 text-sm font-semibold text-slate-600">
          <a href="#features" className="hover:text-slate-900">Features</a><a href="#how" className="hover:text-slate-900">How it works</a><a href="#benefits" className="hover:text-slate-900">Benefits</a>
        </nav>
        <Link to="/login" className="rounded-full bg-slate-900 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700 transition-colors" data-testid="home-login-btn">Sign in</Link>
      </header>

      <section className="px-6 md:px-12 pt-16 pb-20 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center max-w-7xl">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.25em] text-blue-700 mb-4">Swami Software — Your Factory Manager</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]" style={{ fontFamily: 'Chivo' }}>OEE, job cards and quality control for machine shops — in one place.</h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 max-w-2xl">Swami Software connects to your CNC and VMC machines, counts every cycle, records every loss and guides QA through setup approval and in-process inspection — without paper.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/login" style={{ background: BRAND_GRADIENT }} className="rounded-full px-7 py-3 font-bold text-white hover:brightness-110 transition-[filter] inline-flex items-center gap-2" data-testid="home-get-started-btn">Sign in to your factory <ArrowRight className="h-4 w-4" /></Link>
            <a href="mailto:swamisoftwareinfo@gmail.com" className="rounded-full border-2 border-slate-900 px-6 py-3 font-bold hover:bg-slate-900 hover:text-white transition-colors" data-testid="home-contact-btn">Contact us</a>
          </div>
        </div>
        <div className="relative rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-xl">
          <img src="/login-left.jpg" alt="CNC machines on the shop floor" className="w-full h-80 object-cover object-center" />
          <div className="absolute bottom-4 left-4 right-4 grid grid-cols-3 gap-2 text-center">
            {[['OEE', '86%', 'bg-emerald-600'], ['Running', '11 / 12', 'bg-slate-900'], ['Alarms', '1', 'bg-red-600']].map(([l, v, c]) => (
              <div key={l} className={`${c} text-white rounded-xl py-3`}><div className="text-xl font-black">{v}</div><div className="text-[10px] uppercase tracking-widest opacity-80">{l}</div></div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="px-6 md:px-12 pb-20 max-w-7xl">
        <H2 kicker="Features" title="Everything the shop floor needs, connected to the machine" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(([Icon, title, text]) => (
            <div key={title} className="bg-white border border-slate-200 rounded-xl p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow]">
              <Icon className="h-7 w-7 text-blue-700" /><h3 className="mt-4 text-lg font-bold">{title}</h3><p className="mt-2 text-sm text-slate-600">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="how" className="bg-slate-900 text-white px-6 md:px-12 py-20" data-testid="home-how-it-works">
        <div className="max-w-7xl">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-amber-400 mb-3">How it works</p>
          <h2 className="text-3xl sm:text-4xl font-black max-w-3xl mb-12" style={{ fontFamily: 'Chivo' }}>From machine signal to management report in five steps</h2>
          <ol className="grid gap-6 md:grid-cols-5">
            {STEPS.map(([Icon, title, text], i) => (
              <li key={title} className="relative border-t-2 border-white/15 pt-6">
                <span className="absolute -top-4 left-0 h-8 w-8 rounded-full text-sm font-black flex items-center justify-center text-slate-900" style={{ background: BRAND_GRADIENT }}>{i + 1}</span>
                <Icon className="h-7 w-7 text-amber-300" /><h3 className="mt-4 font-bold text-lg">{title}</h3><p className="mt-2 text-sm text-slate-300">{text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="benefits" className="px-6 md:px-12 py-20 max-w-7xl" data-testid="home-benefits">
        <H2 kicker="Benefits & numbers" title="What changes on your shop floor" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-12">
          {NUMBERS.map(([n, t]) => (
            <div key={t} className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="text-4xl font-black bg-clip-text text-transparent" style={{ backgroundImage: BRAND_GRADIENT, fontFamily: 'Chivo' }}>{n}</div>
              <p className="mt-2 text-sm text-slate-600">{t}</p>
            </div>
          ))}
        </div>
        <ul className="grid gap-3 md:grid-cols-2">
          {BENEFITS.map(b => <li key={b} className="flex gap-3 items-start text-slate-700"><span className="mt-2 h-2 w-2 rounded-full bg-blue-700 shrink-0" />{b}</li>)}
        </ul>
        <p className="mt-6 text-xs text-slate-400">Figures are typical examples; results depend on your machines and processes.</p>
      </section>

      <section className="px-6 md:px-12 pb-16 max-w-4xl text-sm text-slate-600" data-testid="home-google-info">
        <h2 className="text-lg font-bold text-slate-900 mb-2">How we use Google Drive</h2>
        <p>Company administrators can connect their own Google Drive to store quality documents. Swami Software only accesses the files and folders it creates or that you select (Google <code>drive.file</code> permission), and uses them only to show drawings and generate inspection reports for your company. See our <a href="/privacy-policy.html" className="text-blue-700 underline">Privacy Policy</a>.</p>
      </section>

      <section className="mx-6 md:mx-12 mb-16 rounded-3xl px-8 py-12 text-white flex flex-wrap items-center justify-between gap-6" style={{ background: BRAND_GRADIENT }}>
        <h2 className="text-2xl sm:text-3xl font-black max-w-xl" style={{ fontFamily: 'Chivo' }}>See your real OEE — machine by machine, shift by shift.</h2>
        <a href="mailto:swamisoftwareinfo@gmail.com" className="rounded-full bg-white text-slate-900 px-7 py-3 font-bold hover:bg-slate-900 hover:text-white transition-colors" data-testid="home-cta-contact">Talk to us</a>
      </section>

      <footer className="border-t border-slate-300 px-6 md:px-12 py-6 flex flex-wrap gap-6 text-sm text-slate-600">
        <span>© {new Date().getFullYear()} Swami Software</span>
        <a href="/privacy-policy.html" className="hover:text-slate-900 underline" data-testid="home-privacy-link">Privacy Policy</a>
        <a href="/terms-of-service.html" className="hover:text-slate-900 underline" data-testid="home-terms-link">Terms of Service</a>
        <a href="mailto:swamisoftwareinfo@gmail.com" className="hover:text-slate-900 underline">swamisoftwareinfo@gmail.com</a>
      </footer>
    </div>
  );
}
